import React from 'react';
import classNames from 'classnames';
import { CheckboxProps } from './types';

/**
 * Checkbox 컴포넌트
 *
 * Figma 디자인 시스템 기반 Checkbox 컴포넌트
 *
 * @example
 * ```tsx
 * <Checkbox
 *   checked={checked}
 *   onChange={setChecked}
 *   label="Checkbox Text"
 * />
 * ```
 *
 * 색상 참고 (Figma):
 * - Border Idle: #E6E9F5
 * - Border Hover/Pressed: #DADDEB
 * - Checked Idle: #3455FC
 * - Checked Hover: #193AE3
 * - Checked Pressed: #0223C9
 * - Checked Disabled: #C7D0FF
 * - Background Pressed: #FAFBFF
 * - Background Disabled: #F2F4FC
 * - Text: #222638
 * - Text Disabled: #C3C7D9
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked = false,
      onChange,
      label,
      disabled = false,
      frame = false,
      variant = 'blue',
      boxSize = 'md',
      className,
      containerClassName,
      name,
      id,
      ...rest
    },
    ref
  ) => {
    // 체크박스 변경 핸들러
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        onChange?.(e.target.checked);
      }
    };

    const gapClass = boxSize === 'sm' ? 'gap-4' : 'gap-4';
    const frameGapClass = boxSize === 'sm' ? 'gap-4' : 'gap-[10px]';
    const boxSizeClass = boxSize === 'sm' ? 'w-5 h-5 rounded-md' : 'w-8 h-8 rounded-lg';
    const iconSizeClass = boxSize === 'sm' ? 'w-4 h-4' : 'w-6 h-6';

    // Container 클래스 (전체 래퍼)
    const containerClasses = classNames(
      'inline-flex items-center',
      {
        // Frame이 있을 때
        'border-[1.5px] rounded-[10px] p-3 w-[260px]': frame,
        'border-[#E6E9F5]': frame && !disabled,
        'border-[#DADDEB]': frame && disabled,
        'bg-[#F2F4FC]': frame && disabled,
        'hover:border-[#DADDEB]': frame && !disabled,
        'active:border-[#DADDEB]': frame && !disabled,
        'active:bg-[#FAFBFF]': frame && !disabled,

        // Frame이 없을 때
        [gapClass]: !frame && label,
      },
      containerClassName
    );

    // Wrapper 클래스 (체크박스 + 라벨)
    const wrapperClasses = classNames('inline-flex items-center', {
      [gapClass]: label,
      [frameGapClass]: frame && label,
    });

    // 체크박스 박스 클래스
    const checkboxBoxClasses = classNames(
      `relative flex items-center justify-center border-[1.5px] transition-all duration-200 cursor-pointer flex-shrink-0 ${boxSizeClass}`,
      {
        // Unchecked 상태
        'bg-white': !checked && !disabled,
        'border-[#E6E9F5]': !checked && !disabled,
        'hover:border-[#DADDEB]': !checked && !disabled,
        'active:border-[#DADDEB]': !checked && !disabled,
        'active:bg-[#FAFBFF]': !checked && !disabled && variant === 'blue',
        'active:bg-[#FAF8FF]': !checked && !disabled && variant === 'purple',

        // Unchecked Disabled
        'bg-[#F2F4FC]': !checked && disabled,
        'border-[#DADDEB]': !checked && disabled,

        // Checked 상태 - Blue variant (환자용)
        'bg-[#3455FC]': checked && !disabled && variant === 'blue',
        'border-[#3455FC]': checked && !disabled && variant === 'blue',
        'hover:bg-[#193AE3]': checked && !disabled && variant === 'blue',
        'hover:border-[#193AE3]': checked && !disabled && variant === 'blue',
        'active:bg-[#0223C9]': checked && !disabled && variant === 'blue',
        'active:border-[#0223C9]': checked && !disabled && variant === 'blue',

        // Checked 상태 - Purple variant (의료진용)
        'bg-[#8255FF]': checked && !disabled && variant === 'purple',
        'border-[#8255FF]': checked && !disabled && variant === 'purple',
        'hover:bg-[#754DE5]': checked && !disabled && variant === 'purple',
        'hover:border-[#754DE5]': checked && !disabled && variant === 'purple',
        'active:bg-[#6844CC]': checked && !disabled && variant === 'purple',
        'active:border-[#6844CC]': checked && !disabled && variant === 'purple',

        // Checked Disabled - Blue variant
        'bg-[#C7D0FF]': checked && disabled && variant === 'blue',
        'border-[#C7D0FF]': checked && disabled && variant === 'blue',

        // Checked Disabled - Purple variant
        'bg-[#CCBAFF]': checked && disabled && variant === 'purple',
        'border-[#CCBAFF]': checked && disabled && variant === 'purple',

        // Disabled cursor
        'cursor-not-allowed': disabled,
      },
      className
    );

    // 라벨 텍스트 클래스
    const labelClasses = classNames('paragraph-medium select-none', {
      'text-[#222638]': !disabled,
      'text-[#C3C7D9]': disabled,
      'cursor-pointer': !disabled,
      'cursor-not-allowed': disabled,
      'ml-2': boxSize === 'sm',
    });

    // Check 아이콘 클래스
    const checkIconClasses = `text-white ${iconSizeClass}`;

    return (
      <div className={containerClasses}>
        <label className={wrapperClasses}>
          {/* 체크박스 박스 */}
          <div className={checkboxBoxClasses}>
            {/* Check 아이콘 (Figma SVG) */}
            {checked && (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={checkIconClasses}
              >
                <path
                  d="M5.88541 10.7771L10.7771 15.6687L18.1146 8.33125"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}

            {/* Hidden Input */}
            <input
              ref={ref}
              type="checkbox"
              checked={checked}
              onChange={handleChange}
              disabled={disabled}
              name={name}
              id={id}
              className="absolute opacity-0 w-0 h-0"
              {...rest}
            />
          </div>

          {/* 라벨 텍스트 */}
          {label && <span className={labelClasses}>{label}</span>}
        </label>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
